import { useQuery } from '@tanstack/react-query'
import { Ghost, Moon, TrendingDown, TrendingUp } from 'lucide-react'
import { accountApi, convictionApi } from '../api/client'
import { relativeTime } from '../lib/relativeTime'
import type { Book, BookRole } from '../types'
import { BookSparkline } from './BookSparkline'
import './BookCard.css'

const HELD_CAP = 25

const ROLE_LABEL: Record<BookRole, string> = {
  research: 'Research',
  validation: 'Validation',
  live: 'Live',
  book: 'Book',
}

const num = (v: unknown): number => {
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : 0
}
const fmtUSD = (v: unknown): string =>
  `$${num(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
const fmtSignedUSD = (v: unknown): string => {
  const n = num(v)
  if (n === 0) return '$0.00'
  return `${n > 0 ? '+' : '−'}$${Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

interface BookCardProps {
  book: Book | undefined
  role: BookRole
}

/** Ghost placeholder for a role with no matching account in the registry. */
function GhostCard({ role }: { role: BookRole }) {
  return (
    <div className="card book-card book-card--ghost">
      <div className="book-card-role-badge book-card-role-badge--ghost">
        <Ghost size={13} aria-hidden="true" />
        {ROLE_LABEL[role]}
      </div>
      <div className="book-card-ghost-text">not yet created</div>
    </div>
  )
}

export function BookCard({ book, role }: BookCardProps) {
  // Hooks must run unconditionally — call before any early return.
  const convictionQuery = useQuery({
    queryKey: ['conviction-status', book?.id],
    queryFn: () => convictionApi.status(book?.id),
    enabled: !!book,
    retry: 1,
    refetchInterval: 30000,
  })

  // Per-book equity. The queryKey MUST carry book.id — a bare ['account'] key
  // would make every card share one cache entry and render whichever book
  // fetched last. A dormant/registered-but-not-live book 404s; that is a
  // normal state (the equity block is simply omitted), so don't retry it.
  const accountQuery = useQuery({
    queryKey: ['account', book?.id],
    queryFn: () => accountApi.get(book?.id),
    enabled: !!book,
    retry: false,
    refetchInterval: 10000,
  })

  if (!book) return <GhostCard role={role} />

  const status = book.bookStatus
  const isDormant = status === null
  const isHalted = status?.halted === true
  const isLiveDormant = role === 'live' && (isDormant || isHalted)

  const held = convictionQuery.data?.candidates_by_state?.held ?? 0
  const heldPct = Math.min(100, Math.round((held / HELD_CAP) * 100))

  // Render the equity block only once the book's own figures have resolved —
  // a dormant book (404) or an in-flight first fetch would otherwise print a
  // misleading $0.00 / +0.00%.
  const account = accountQuery.data
  const hasEquity = !!account
  const dailyPnl = num(account?.daily_pnl)
  const equity = num(account?.account_value)
  const dayPct = equity - dailyPnl > 0 ? (dailyPnl / (equity - dailyPnl)) * 100 : 0

  let statusLine: { text: string; cls: string }
  if (isDormant) {
    statusLine = { text: 'dormant · not in engine', cls: 'muted' }
  } else if (isHalted) {
    statusLine = { text: status.halt_reason ? `halted: ${status.halt_reason}` : 'halted', cls: 'warn' }
  } else {
    statusLine = { text: 'running', cls: 'gain' }
  }

  return (
    <div className={`card book-card book-card--${role}${isLiveDormant ? ' book-card--dormant' : ''}`}>
      <div className="book-card-header">
        <span className={`book-card-role-badge book-card-role-badge--${role}`}>
          {ROLE_LABEL[role]}
        </span>
        {isLiveDormant && (
          <span className="book-card-dormant-chip">
            <Moon size={11} aria-hidden="true" />
            DORMANT · built-but-OFF
          </span>
        )}
      </div>

      <div className="book-card-identity">
        <div className="book-card-label">{book.label ?? `Account ${book.id}`}</div>
        <div className="book-card-account-id mono">{book.account_id}</div>
      </div>

      <div className={`book-card-status book-card-status--${statusLine.cls}`}>
        <span className="book-card-status-dot" aria-hidden="true" />
        {statusLine.text}
      </div>

      {hasEquity && (
        <div className="book-card-equity">
          <div className="book-card-equity-main">
            <span className="book-card-equity-value num">{fmtUSD(account.account_value)}</span>
            <span className={`book-card-equity-delta num ${dailyPnl >= 0 ? 'delta-up' : 'delta-down'}`}>
              {dailyPnl >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
              {fmtSignedUSD(account.daily_pnl)} ({dayPct >= 0 ? '+' : ''}{dayPct.toFixed(2)}%)
            </span>
          </div>
          <div className="book-card-equity-sub">
            <span>Cash <span className="num">{fmtUSD(account.cash)}</span></span>
          </div>
          <BookSparkline book={book} />
        </div>
      )}

      <div className="book-card-held">
        <div className="book-card-held-header">
          <span className="book-card-held-label">Held / cap</span>
          <span className="book-card-held-value num">{held} / {HELD_CAP}</span>
        </div>
        <div className="book-card-held-track">
          <div className="book-card-held-fill" style={{ width: `${heldPct}%` }} />
        </div>
      </div>

      <div className="book-card-footer">
        <span>Last cycle <span className="num">{relativeTime(status?.last_cycle_at)}</span></span>
        <span>Overlay v<span className="num">{status?.overlay_version ?? '—'}</span></span>
      </div>
    </div>
  )
}
